'use client'

import { useSearchParams } from 'next/navigation'
import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react'

interface SheetContextType {
  openSheetId: SHEET | null
  openSheet: (sheetId: SHEET) => void
  closeSheet: () => void
  isSheetOpen: (sheetId: SHEET) => boolean
}

const SheetContext = createContext<SheetContextType | undefined>(undefined)

export const SheetProvider = ({ children }: { children: ReactNode }) => {
  const searchParams = useSearchParams()
  const [openSheetId, setOpenSheetId] = useState<SHEET | null>(null)

  const openSheet = useCallback((sheetId: SHEET) => {
    setOpenSheetId(sheetId)
  }, [])

  const closeSheet = useCallback(() => {
    setOpenSheetId(null)
  }, [])

  const isSheetOpen = useCallback((sheetId: SHEET) => openSheetId === sheetId, [openSheetId])

  // Open sheet via ?sheet=X URL param
  useEffect(() => {
    const sheetId = searchParams.get('sheet')

    if (sheetId && validSheets.includes(sheetId as SHEET)) {
      openSheet(sheetId as SHEET)
    }
  }, [openSheet, searchParams])

  // Memoize the context value to ensure stable references
  const value = useMemo(
    () => ({
      openSheetId,
      openSheet,
      closeSheet,
      isSheetOpen,
    }),
    [openSheetId, openSheet, closeSheet, isSheetOpen],
  )

  return <SheetContext.Provider value={value}>{children}</SheetContext.Provider>
}

export const useSheet = () => {
  const context = useContext(SheetContext)

  if (!context) {
    throw new Error('useSheet must be used within a SheetProvider')
  }

  return context
}

export enum SHEET {
  MENU = 'menu',
  CART = 'cart',
  WISHLIST = 'wishlist',
  FILTERS = 'filters',
  SIZING = 'sizing',
  SEARCH = 'search',
  GIFT_CARD = 'gift-card',
}

const validSheets = Object.values<SHEET>(SHEET)
